/**
 * Mixpanel helpers for Ask Asper composer slash-command (entity filter / time period).
 */

import type { LookupItem } from '../AskAsperAgentic.types';
import {
    isDimensionCategoryId,
    normalizeColumnKey,
} from '../SearchBar/lookupEntityTypes';
import type { MixpanelEventProperty, MixpanelTrackEventCallback } from './mixpanelEvents';

export const COMPOSER_SLASH_COMMAND_OPTION_SELECT = 'ask_asper_composer_slash_command_option_select';
export const COMPOSER_ENTITY_FILTER_ITEM_SELECT = 'ask_asper_composer_entity_filter_item_select';
export const COMPOSER_ENTITY_FILTER_ITEM_DESELECT = 'ask_asper_composer_entity_filter_item_deselect';
export const COMPOSER_TIME_PERIOD_SELECT = 'ask_asper_composer_time_period_select';
export const COMPOSER_QUERY_SEND_WITH_SLASH_FILTERS = 'ask_asper_composer_query_send_with_slash_filters';

export const TIME_PERIOD_COLUMN = 'Time Period';

export interface SlashCommandAnalyticsContext {
    threadId?: string;
    sessionId?: string;
}

/**
 * Map internal category ids to stable analytics `command_name` values.
 * Tier-1 ids may be legacy (`entity`), normalized columns (`brand`), or
 * dimension drills (`dimension:product`); dashboards expect only
 * `entity_filter` and `time_period`.
 */
export function toSlashCommandName(categoryId: string): string {
    const lookupKey = isDimensionCategoryId(categoryId)
        ? categoryId.slice('dimension:'.length)
        : categoryId;
    const normalized = normalizeColumnKey(lookupKey);
    if (normalized === 'time period' || categoryId === 'time_period') {
        return 'time_period';
    }
    return 'entity_filter';
}

export function isEntityLookupItem(item: LookupItem): boolean {
    return item.column_name !== TIME_PERIOD_COLUMN;
}

export function isTimePeriodLookupItem(item: LookupItem): boolean {
    return item.column_name === TIME_PERIOD_COLUMN;
}

export function toEntityAnalyticsRecord(item: LookupItem): MixpanelEventProperty {
    return {
        dimension: item.column_name,
        entity_id: `${item.column_name}::${item.entity_name}`,
        entity_name: item.entity_name,
    };
}

function withAnalyticsContext(
    ctx: SlashCommandAnalyticsContext | undefined,
    props: MixpanelEventProperty = {},
): MixpanelEventProperty {
    return {
        ...props,
        ...(ctx?.threadId ? { thread_id: ctx.threadId } : {}),
        ...(ctx?.sessionId ? { session_id: ctx.sessionId } : {}),
    };
}

export function fireSlashCommandOptionSelect(
    fire: MixpanelTrackEventCallback,
    categoryId: string,
    categoryLabel: string,
    ctx?: SlashCommandAnalyticsContext,
): void {
    fire(
        COMPOSER_SLASH_COMMAND_OPTION_SELECT,
        withAnalyticsContext(ctx, {
            command_name: toSlashCommandName(categoryId),
            command_label: categoryLabel,
        }),
    );
}

export function fireEntityFilterItemSelect(
    fire: MixpanelTrackEventCallback,
    selectedLookups: LookupItem[],
    ctx?: SlashCommandAnalyticsContext,
): void {
    const entities = selectedLookups.filter(isEntityLookupItem).map(toEntityAnalyticsRecord);
    if (entities.length === 0) return;
    fire(
        COMPOSER_ENTITY_FILTER_ITEM_SELECT,
        withAnalyticsContext(ctx, {
            selected_entities: entities,
            selection_count: entities.length,
        }),
    );
}

export function fireEntityFilterItemDeselect(
    fire: MixpanelTrackEventCallback,
    item: LookupItem,
    ctx?: SlashCommandAnalyticsContext,
): void {
    if (!isEntityLookupItem(item)) return;
    fire(
        COMPOSER_ENTITY_FILTER_ITEM_DESELECT,
        withAnalyticsContext(ctx, {
            deselected_entity: toEntityAnalyticsRecord(item),
        }),
    );
}

export function fireTimePeriodSelect(
    fire: MixpanelTrackEventCallback,
    item: LookupItem,
    ctx?: SlashCommandAnalyticsContext,
): void {
    fire(
        COMPOSER_TIME_PERIOD_SELECT,
        withAnalyticsContext(ctx, {
            time_period_label: item.entity_name,
        }),
    );
}

/** True when the query includes at least one confirmed slash-command entity or time-period token. */
export function queryHasSlashFilters(query: string, selectedLookups: LookupItem[] | undefined): boolean {
    if (!query.trim() || !selectedLookups?.length) return false;
    return selectedLookups.some((item) => query.includes(item.entity_name));
}

export function fireQuerySendWithSlashFilters(
    fire: MixpanelTrackEventCallback,
    query: string,
    ctx?: SlashCommandAnalyticsContext,
): void {
    fire(
        COMPOSER_QUERY_SEND_WITH_SLASH_FILTERS,
        withAnalyticsContext(ctx, {
            query,
        }),
    );
}
